import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import StoryShareButtons from '../components/StoryShareButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve a story's list of image URLs (supports both the new photo_urls array and legacy photo_url)
const getStoryImages = (s) => {
  if (s && Array.isArray(s.photo_urls) && s.photo_urls.length) return s.photo_urls;
  if (s && s.photo_url) return [s.photo_url];
  return [];
};

// Resolve the matching object-position string for a given image index.
// Defaults to '50% 50%' (centered) when no position has been set in CRM.
const getImagePosition = (s, idx) => {
  const positions = (s && Array.isArray(s.photo_positions)) ? s.photo_positions : [];
  return positions[idx] || '50% 50%';
};

const absUrl = (u) => (u.startsWith('http') ? u : `${BACKEND_URL}${u}`);

const ImpactStories = () => {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [active, setActive] = useState(null);
  const [activeImgIdx, setActiveImgIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/stories`);
        if (alive) setStories(data || []);
      } catch (e) {
        if (alive) setError('Unable to load stories right now.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Deep-link support: if URL has ?story=<id>, auto-open that story when stories load.
  useEffect(() => {
    if (!stories || stories.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const targetId = params.get('story');
    if (!targetId) return;
    const match = stories.find((s) => String(s.id) === String(targetId));
    if (match) {
      setActive(match);
      setActiveImgIdx(0);
    }
  }, [stories]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-emerald-50">
      <Helmet>
        <title>Impact Stories | Kioo Radio 98.1FM</title>
        <meta name="description" content="Real stories of lives transformed through Kioo Radio 98.1FM's broadcasts across Liberia, Sierra Leone, and Guinea." />
      </Helmet>

      {/* Hero */}
      <section className="relative bg-gradient-to-r from-emerald-700 via-emerald-600 to-amber-600 text-white py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.08'%3E%3Cpath d='M50 50c0-5.523-4.477-10-10-10s-10 4.477-10 10 4.477 10 10 10 10-4.477 10-10zm-10 30a30 30 0 110-60 30 30 0 010 60z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}></div>
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-emerald-100 text-sm mb-5">
            <span className="w-2 h-2 bg-amber-300 rounded-full animate-pulse"></span>
            Real Lives, Real Impact
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">Impact Stories</h1>
          <p className="text-lg md:text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
            Stories of hope, healing, and transformation from listeners across Liberia, Sierra Leone, and Guinea.
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4">
          {loading && <div className="text-center py-16 text-gray-500" data-testid="stories-loading">Loading stories…</div>}

          {error && !loading && (
            <div className="text-center py-16 text-red-600" data-testid="stories-error">{error}</div>
          )}

          {!loading && !error && stories.length === 0 && (
            <div className="text-center py-16" data-testid="stories-empty">
              <div className="text-6xl mb-4">📖</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">New stories are coming soon</h3>
              <p className="text-gray-600">Our team is collecting testimonies from across the region. Check back soon.</p>
            </div>
          )}

          {!loading && !error && stories.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stories.map(story => {
                const publishDate = story.published_at ? new Date(story.published_at) : null;
                const imgs = getStoryImages(story);
                const cover = imgs[0];
                return (
                  <article
                    key={story.id}
                    data-testid={`story-card-${story.id}`}
                    className="group bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 cursor-pointer flex flex-col"
                    onClick={() => { setActive(story); setActiveImgIdx(0); }}
                  >
                    <div className="relative aspect-[16/9] overflow-hidden bg-gradient-to-br from-emerald-500 to-amber-500">
                      {cover ? (
                        <img
                          src={absUrl(cover)}
                          alt={story.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          style={{ objectPosition: getImagePosition(story, 0) }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-6xl opacity-80">📖</div>
                      )}
                      {imgs.length > 1 && (
                        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {imgs.length} photos
                        </div>
                      )}
                    </div>
                    <div className="p-5 flex-1 flex flex-col">
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                        {story.location && <span>📍 {story.location}</span>}
                        {story.location && publishDate && <span>•</span>}
                        {publishDate && <span>{publishDate.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-kioo-primary transition-colors">
                        {story.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-4 flex-1">{story.body}</p>
                      <div className="mt-4 flex items-center justify-between text-sm">
                        {story.author && <span className="text-gray-500 truncate">— {story.author}</span>}
                        <span className="text-kioo-primary font-semibold group-hover:underline whitespace-nowrap">Read more →</span>
                      </div>
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-xs text-gray-500">Share this story</span>
                        <StoryShareButtons story={story} variant="card" />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Story Detail Modal */}
      {active && (() => {
        const modalImgs = getStoryImages(active);
        const safeIdx = Math.max(0, Math.min(activeImgIdx, modalImgs.length - 1));
        const prev = () => setActiveImgIdx(i => (i - 1 + modalImgs.length) % modalImgs.length);
        const next = () => setActiveImgIdx(i => (i + 1) % modalImgs.length);
        return (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="story-modal-overlay"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="story-modal"
          >
            <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-kioo-primary to-kioo-secondary text-white">
              <h3 className="font-bold text-lg truncate">{active.title}</h3>
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                data-testid="story-modal-close"
                className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto">
              {modalImgs.length > 0 && (
                <div className="relative bg-black">
                  <img
                    src={absUrl(modalImgs[safeIdx])}
                    alt={`${active.title} — photo ${safeIdx + 1}`}
                    className="w-full max-h-[60vh] object-contain"
                    data-testid={`story-modal-image-${safeIdx}`}
                  />
                  {modalImgs.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={prev}
                        aria-label="Previous photo"
                        data-testid="story-modal-prev"
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/75 text-white flex items-center justify-center"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={next}
                        aria-label="Next photo"
                        data-testid="story-modal-next"
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/75 text-white flex items-center justify-center"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 bg-black/40 px-3 py-1.5 rounded-full">
                        {modalImgs.map((_, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => setActiveImgIdx(i)}
                            aria-label={`Go to photo ${i + 1}`}
                            className={`w-2 h-2 rounded-full transition ${i === safeIdx ? 'bg-white' : 'bg-white/40 hover:bg-white/70'}`}
                          />
                        ))}
                      </div>
                      <div className="absolute top-3 right-3 bg-black/60 text-white text-xs font-semibold px-2 py-1 rounded">
                        {safeIdx + 1} / {modalImgs.length}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                  {active.location && <span>📍 {active.location}</span>}
                  {active.author && <span>— {active.author}</span>}
                  {active.published_at && <span>{new Date(active.published_at).toLocaleDateString()}</span>}
                </div>
                <div className="prose max-w-none text-gray-800 whitespace-pre-wrap leading-relaxed">{active.body}</div>
                <div className="mt-6 pt-5 border-t border-gray-200">
                  <StoryShareButtons story={active} variant="modal" />
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default ImpactStories;
